import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, Mail, Car, AlertTriangle, Check, Loader2, Save, Eye, EyeOff, Undo2, CircleDot, Heart, Footprints, DollarSign, MessageCircle, Handshake, Plus, IdCard, MapPin, Calendar, Target, ArrowRight, TrendingUp, TrendingDown, Minus, ChevronRight, AlertOctagon, CheckCircle2 } from 'lucide-react';
import { diffWords } from 'diff';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { StatusDot, PriorityBadge, StageBadge } from '@/components/StatusBadge';
import { useDealershipData } from '@/data/hooks/useDealershipData';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import type { TablesUpdate } from '@/integrations/supabase/types';
import { timeAgo } from '@/data/mockData';
import {
  NEXT_STEP_LABELS,
  STAGE_LABELS,
  STAGE_ORDER,
  DEMO_TYPE_LABELS,
} from '@/types';
import type { ActivityType, DealStage, DealStatus, NextStep, DemoType } from '@/types';
import { toast } from 'sonner';
import { TemplateChips } from '@/components/TemplateChips';
import { WishlistSheet } from '@/components/wishlist/WishlistSheet';
import { LinkedProgramsCard } from '@/components/programs/LinkedProgramsCard';
import { track } from '@/lib/telemetry';
import { computeMomentum } from '@/lib/stall';
import { ComposeMessageDialog } from '@/components/comms/ComposeMessageDialog';
import { ConversationThreadCard } from '@/components/comms/ConversationThreadCard';

const STATUS_LABELS: Record<DealStatus, string> = {
  active: 'Active',
  slowing: 'Slowing',
  no_activity: 'No Activity',
};

const NEXT_STEP_VERBS: Record<NextStep, string> = {
  call: 'Call Customer',
  follow_up: 'Send Follow-Up',
  appointment: 'Book Appointment',
  send_numbers: 'Send Numbers',
  trade_review: 'Review Trade',
  close: 'Close Deal Today',
  sold: 'Mark Sold',
  delivery: 'Schedule Delivery',
};

const SAVE_LABELS: Partial<Record<ActivityType, string>> = {
  walkaround: 'Complete Walkaround',
  test_drive: 'Log Test Drive',
  quote_review: 'Log Quote Review',
  phone_call: 'Save Call Notes',
  followup_video: 'Send Follow-Up Video',
  appointment_set: 'Confirm Appointment',
  text_sent: 'Log Text Sent',
  email_sent: 'Log Email Sent',
  note_added: 'Save Note',
};

const ACTIVITY_DOT_COLORS: Record<string, string> = {
  phone_call: 'bg-blue-400',
  test_drive: 'bg-primary',
  walkaround: 'bg-amber-400',
  quote_review: 'bg-violet-400',
  email_sent: 'bg-cyan-400',
  text_sent: 'bg-cyan-400',
  followup_video: 'bg-cyan-400',
  appointment_set: 'bg-emerald-400',
  note_added: 'bg-muted-foreground/60',
};

function DiffPreview({ before, after }: { before: string; after: string }) {
  const parts = diffWords(before || '', after || '');
  const hasChange = parts.some(p => p.added || p.removed);
  return (
    <div className="space-y-2 text-xs">
      <div className="font-medium text-muted-foreground">Notes diff preview</div>
      <div className="rounded-md border border-border bg-muted/30 p-2 font-mono whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto">
        {!hasChange && <span className="text-muted-foreground">No changes.</span>}
        {parts.map((part, i) => {
          if (part.added) return <span key={i} className="bg-emerald-500/20 text-emerald-300 rounded px-0.5">{part.value}</span>;
          if (part.removed) return <span key={i} className="bg-red-500/20 text-red-300 line-through rounded px-0.5">{part.value}</span>;
          return <span key={i}>{part.value}</span>;
        })}
      </div>
    </div>
  );
}

function actLabel(t: ActivityType) {
  if (t in DEMO_TYPE_LABELS) return DEMO_TYPE_LABELS[t as DemoType];
  const map: Record<string, string> = {
    text_sent: 'Text Sent',
    email_sent: 'Email Sent',
    note_added: 'Note Added',
  };
  return map[t] || t;
}

function LicenseScanCard({
  imagePath,
  data,
}: {
  imagePath: string | null | undefined;
  data: Record<string, unknown> | null | undefined;
}) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loadErr, setLoadErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSignedUrl(null);
    setLoadErr(false);
    if (!imagePath) return;
    (async () => {
      const { data: signed, error } = await supabase.storage
        .from('license-scans')
        .createSignedUrl(imagePath, 60 * 10);
      if (cancelled) return;
      if (error || !signed?.signedUrl) {
        setLoadErr(true);
      } else {
        setSignedUrl(signed.signedUrl);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [imagePath]);

  if (!imagePath && !data) return null;

  const get = (k: string) => {
    const v = data?.[k];
    return typeof v === 'string' && v.trim().length > 0 ? v : null;
  };
  const fullName = get('full_name') || [get('first_name'), get('last_name')].filter(Boolean).join(' ') || null;
  const address = get('address');
  const dob = get('dob');
  const licenseNo = get('license_no');

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <IdCard className="h-4 w-4" /> License Scan
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex gap-4">
          <div className="shrink-0">
            {signedUrl ? (
              <a href={signedUrl} target="_blank" rel="noreferrer" className="block">
                <img
                  src={signedUrl}
                  alt="Driver license scan"
                  className="h-24 w-36 object-cover rounded-md border border-border bg-muted"
                  onError={() => setLoadErr(true)}
                />
              </a>
            ) : (
              <div className="h-24 w-36 rounded-md border border-border bg-muted flex items-center justify-center text-xs text-muted-foreground">
                {loadErr ? 'Unavailable' : imagePath ? <Loader2 className="h-4 w-4 animate-spin" /> : 'No image'}
              </div>
            )}
          </div>
          <div className="flex-1 space-y-2 text-sm min-w-0">
            {fullName && (
              <div className="flex items-center gap-2">
                <IdCard className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="font-medium truncate">{fullName}</span>
              </div>
            )}
            {address && (
              <div className="flex items-start gap-2">
                <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                <span className="text-muted-foreground break-words">{address}</span>
              </div>
            )}
            {dob && (
              <div className="flex items-center gap-2">
                <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="text-muted-foreground">DOB {dob}</span>
              </div>
            )}
            {licenseNo && (
              <div className="text-xs text-muted-foreground font-mono">#{licenseNo}</div>
            )}
            {!fullName && !address && !dob && !licenseNo && (
              <p className="text-xs text-muted-foreground">No fields extracted from this scan.</p>
            )}
          </div>
        </div>
        <p className="mt-3 text-[10px] text-muted-foreground">
          Verify against the customer in person. Auto-deleted after 30 days.
        </p>
      </CardContent>
    </Card>
  );
}

export default function DealDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { mode, dealershipId, userId } = useAuth();
  const { deals, activities, loading, refresh } = useDealershipData();

  const deal = useMemo(() => deals.find(d => d.id === id), [deals, id]);
  const dealActivities = useMemo(
    () => activities.filter(a => a.deal_id === id),
    [activities, id],
  );

  const logCardRef = useRef<HTMLDivElement>(null);
  const scrollToLog = () => {
    logCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const [savingField, setSavingField] = useState<string | null>(null);
  const [activityType, setActivityType] = useState<ActivityType | ''>('');
  const [activityNotes, setActivityNotes] = useState('');
  const [logging, setLogging] = useState(false);

  // Notes autosave
  const [notesDraft, setNotesDraft] = useState<string>(deal?.notes ?? '');
  const [notesStatus, setNotesStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const lastSavedNotes = useRef<string>(deal?.notes ?? '');
  const notesInitialized = useRef(false);
  // Client-side lock: monotonically increasing request id. Only the latest write wins.
  const notesRequestSeqRef = useRef(0);
  const notesInFlightRef = useRef(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [showDiff, setShowDiff] = useState(false);
  const [isUndoing, setIsUndoing] = useState(false);
  const [, forceTick] = useState(0);
  const [confirmUndo, setConfirmUndo] = useState(false);
  const [previousSaved, setPreviousSaved] = useState<{ value: string; at: Date } | null>(null);
  const [composeChannel, setComposeChannel] = useState<'email' | 'sms' | null>(null);
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const undoConfirmToastRef = useRef<string | number | null>(null);
  const [undoSecondsLeft, setUndoSecondsLeft] = useState(0);

  // Tick every second while an undo window is active
  useEffect(() => {
    if (!previousSaved) {
      setUndoSecondsLeft(0);
      return;
    }
    const update = () => {
      const elapsed = (Date.now() - previousSaved.at.getTime()) / 1000;
      const left = Math.max(0, Math.ceil(30 - elapsed));
      setUndoSecondsLeft(left);
    };
    update();
    const i = setInterval(update, 1000);
    return () => clearInterval(i);
  }, [previousSaved]);
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  const modKey = isMac ? '⌘' : 'Ctrl';

  // Keyboard shortcut: Ctrl/Cmd+Shift+Z anywhere, or Ctrl/Cmd+Z when not focused in the notes textarea
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const key = e.key.toLowerCase();
      // Ctrl/Cmd+S: always intercept on this page to prevent browser save dialog
      if (key === 's') {
        e.preventDefault();
        if (mode === 'real' && notesDraft !== lastSavedNotes.current) {
          saveNotes(notesDraft);
        }
        return;
      }
      const isDirty = notesDraft !== lastSavedNotes.current;
      if (!isDirty || mode !== 'real') return;
      const target = e.target as HTMLElement | null;
      const inTextarea = target?.tagName === 'TEXTAREA';
      if (key === 'z') {
        // Inside the textarea, only override when Shift is also held (so plain Ctrl+Z still does native undo)
        if (inTextarea && !e.shiftKey) return;
        e.preventDefault();
        setConfirmUndo(true);
      } else if (key === 'd') {
        e.preventDefault();
        setShowDiff(s => !s);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notesDraft, mode]);

  // Tick every 30s to refresh "x seconds ago" label
  useEffect(() => {
    const t = setInterval(() => forceTick(n => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  // Initialize / re-sync notes when the deal loads or changes externally
  useEffect(() => {
    if (!deal) return;
    if (!notesInitialized.current) {
      setNotesDraft(deal.notes ?? '');
      lastSavedNotes.current = deal.notes ?? '';
      setLastSavedAt(deal.last_activity_at ? new Date(deal.last_activity_at) : null);
      notesInitialized.current = true;
    } else if ((deal.notes ?? '') !== lastSavedNotes.current && notesStatus !== 'saving') {
      // External change — accept it as the new baseline
      lastSavedNotes.current = deal.notes ?? '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal?.id, deal?.notes]);

  // In real mode, hydrate notes from the dedicated deal_notes table (source of truth).
  // Falls back silently to whatever was seeded from the deal row.
  useEffect(() => {
    if (mode !== 'real' || !deal) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('deal_notes')
        .select('content, updated_at')
        .eq('deal_id', deal.id)
        .maybeSingle();
      if (cancelled || error || !data) return;
      // Only adopt if the user hasn't started typing a different draft
      if (notesDraft === lastSavedNotes.current) {
        setNotesDraft(data.content ?? '');
      }
      lastSavedNotes.current = data.content ?? '';
      if (data.updated_at) setLastSavedAt(new Date(data.updated_at));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal?.id, mode]);

  // Single mutex-style writer for notes. Latest call wins; earlier in-flight results are discarded.
  // Returns 'success' | 'error' | 'skipped' so callers (e.g. undo) can react to the final outcome.
  const saveNotes = async (
    value: string,
    opts?: { silent?: boolean }
  ): Promise<'success' | 'error' | 'skipped'> => {
    if (!deal || mode !== 'real' || !dealershipId) return 'skipped';
    if (value === lastSavedNotes.current) return 'skipped';

    const mySeq = ++notesRequestSeqRef.current;
    const previousValue = lastSavedNotes.current;

    // Wait for any in-flight write to release the lock
    while (notesInFlightRef.current) {
      await new Promise(r => setTimeout(r, 30));
      // A newer request superseded us — abort
      if (mySeq !== notesRequestSeqRef.current) return 'skipped';
    }

    // Re-check after acquiring the lock
    if (mySeq !== notesRequestSeqRef.current) return 'skipped';
    if (value === lastSavedNotes.current) return 'skipped';

    notesInFlightRef.current = true;
    setNotesStatus('saving');

    try {
      const { error } = await supabase
        .from('deal_notes')
        .upsert(
          {
            deal_id: deal.id,
            dealership_id: dealershipId,
            content: value,
            updated_by: userId,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'deal_id' },
        );

      // Keep the deal's last_activity_at in sync so dashboards reflect the edit.
      if (!error) {
        await supabase
          .from('deals')
          .update({ last_activity_at: new Date().toISOString() })
          .eq('id', deal.id);
      }

      // Stale response — newer request is queued; discard our outcome
      if (mySeq !== notesRequestSeqRef.current) return 'skipped';

      if (error) {
        setNotesStatus('error');
        if (!opts?.silent) {
          toast.error('Failed to save notes', { description: error.message });
        }
        return 'error';
      }

      await supabase.from('activities').insert({
        dealership_id: dealershipId,
        deal_id: deal.id,
        rep_id: userId,
        activity_type: 'note_added',
        details: value ? value.slice(0, 240) : 'Notes cleared',
        customer_name: deal.customer_name,
      });

      if (mySeq !== notesRequestSeqRef.current) return 'skipped';

      lastSavedNotes.current = value;
      setNotesStatus('saved');
      setLastSavedAt(new Date());
      setPreviousSaved({ value: previousValue, at: new Date() });
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
      undoTimerRef.current = setTimeout(() => setPreviousSaved(null), 30_000);
      if (!opts?.silent) {
        toast.success('Notes saved', {
          action: { label: 'Undo', onClick: () => undoLastSave(previousValue) },
          duration: 8_000,
        });
      }
      refresh();
      return 'success';
    } finally {
      notesInFlightRef.current = false;
    }
    return 'skipped';
  };

  const undoLastSave = async (revertTo: string) => {
    if (!deal || mode !== 'real') return;
    // Bumping the seq immediately invalidates any in-flight save and any older queued undo
    notesRequestSeqRef.current++;
    setPreviousSaved(null);
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    if (undoConfirmToastRef.current !== null) {
      toast.dismiss(undoConfirmToastRef.current);
      undoConfirmToastRef.current = null;
    }
    setNotesDraft(revertTo);
    setIsUndoing(true);
    try {
      const result = await saveNotes(revertTo, { silent: true });
      if (result === 'success') {
        toast.success('Notes reverted', {
          description: 'Your notes were restored to the previous version.',
        });
      } else if (result === 'error') {
        toast.error('Undo failed', {
          description: 'We couldn\u2019t revert your notes. Please try again.',
        });
      }
    } finally {
      setIsUndoing(false);
    }
  };

  const requestUndo = (revertTo: string) => {
    if (!deal || mode !== 'real') return;
    if (isUndoing || notesStatus === 'saving') return;
    // Prevent stacking multiple confirmation toasts during the active window
    if (undoConfirmToastRef.current !== null) {
      toast.dismiss(undoConfirmToastRef.current);
    }
    const id = toast('Undo last save?', {
      description: 'This will revert your notes to the previous version.',
      duration: Math.max(1, undoSecondsLeft) * 1000,
      action: {
        label: 'Confirm undo',
        onClick: () => {
          undoConfirmToastRef.current = null;
          undoLastSave(revertTo);
        },
      },
      cancel: {
        label: 'Keep',
        onClick: () => {
          undoConfirmToastRef.current = null;
        },
      },
      onDismiss: () => { undoConfirmToastRef.current = null; },
      onAutoClose: () => { undoConfirmToastRef.current = null; },
    });
    undoConfirmToastRef.current = id;
  };

  useEffect(() => {
    if (!deal || !notesInitialized.current) return;
    if (notesDraft === lastSavedNotes.current) return;
    if (mode !== 'real' || !dealershipId) return;
    const handle = setTimeout(() => { saveNotes(notesDraft); }, 1200);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notesDraft, deal?.id, mode, dealershipId, userId]);

  // Keyboard shortcuts for the undo flow:
  //  - Ctrl/Cmd+Z (when not focused inside the notes textarea) opens the undo confirmation
  //  - While the confirmation toast is open: Enter confirms, Escape cancels
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      const inEditable =
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        (target?.isContentEditable ?? false);

      // Open undo confirmation
      const isUndoCombo = (e.ctrlKey || e.metaKey) && !e.shiftKey && (e.key === 'z' || e.key === 'Z');
      if (isUndoCombo && !inEditable) {
        if (
          previousSaved &&
          notesDraft === lastSavedNotes.current &&
          undoSecondsLeft > 0 &&
          !isUndoing &&
          notesStatus !== 'saving' &&
          undoConfirmToastRef.current === null
        ) {
          e.preventDefault();
          requestUndo(previousSaved.value);
        }
        return;
      }

      // Confirm / cancel an open undo toast
      if (undoConfirmToastRef.current !== null) {
        if (e.key === 'Enter') {
          if (inEditable) return;
          if (!previousSaved) return;
          e.preventDefault();
          const id = undoConfirmToastRef.current;
          undoConfirmToastRef.current = null;
          toast.dismiss(id);
          undoLastSave(previousSaved.value);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          const id = undoConfirmToastRef.current;
          undoConfirmToastRef.current = null;
          toast.dismiss(id);
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousSaved, notesDraft, undoSecondsLeft, isUndoing, notesStatus]);

  if (loading && !deal) {
    return (
      <div className="p-6 text-center text-muted-foreground flex items-center justify-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading deal...
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">Deal not found.</p>
        <Link to="/dashboard" className="text-primary text-sm mt-2 inline-block">
          Back to dashboard
        </Link>
      </div>
    );
  }

  const isReal = mode === 'real';

  const updateField = async (field: 'stage' | 'status' | 'next_step', value: string) => {
    if (!isReal) {
      toast.info('Editing is available in Real mode only.');
      return;
    }
    // Enforce "every deal needs a next step" before advancing past 'new'.
    if (field === 'stage' && value !== 'new' && value !== 'sold' && !deal.next_step) {
      toast.error('Set a next step before advancing the stage.');
      return;
    }
    setSavingField(field);
    const payload: TablesUpdate<'deals'> = {
      [field]: value,
      last_activity_at: new Date().toISOString(),
    };
    const { error } = await supabase
      .from('deals')
      .update(payload)
      .eq('id', deal.id);
    setSavingField(null);
    if (error) {
      toast.error('Failed to update', { description: error.message });
      return;
    }
    toast.success(`${field.replace('_', ' ')} updated`);
    track(`deal_${field}_updated`, { userId, dealershipId, mode }, { deal_id: deal.id, value });
    refresh();
  };

  const logActivity = async () => {
    if (!activityType) {
      toast.error('Pick an activity type');
      return;
    }
    if (!isReal) {
      toast.info('Activity logging is available in Real mode only.');
      return;
    }
    if (!dealershipId) return;
    setLogging(true);
    const nowIso = new Date().toISOString();
    const { error: actErr } = await supabase.from('activities').insert({
      dealership_id: dealershipId,
      deal_id: deal.id,
      rep_id: userId,
      activity_type: activityType,
      details: activityNotes || null,
      customer_name: deal.customer_name,
    });
    if (!actErr) {
      await supabase
        .from('deals')
        .update({ last_activity_at: nowIso, status: 'active' })
        .eq('id', deal.id);
    }
    setLogging(false);
    if (actErr) {
      toast.error('Failed to log activity', { description: actErr.message });
      return;
    }
    toast.success('Activity logged');
    setActivityType('');
    setActivityNotes('');
    refresh();
  };

  return (
    <div className="space-y-6 pb-20 lg:pb-0 max-w-4xl mx-auto">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{deal.customer_name}</h1>
          <p className="text-sm text-muted-foreground">{deal.vehicle}</p>
        </div>
        <WishlistSheet
          trigger={
            <Button variant="outline" size="sm" className="gap-1.5">
              <Heart className="h-4 w-4" /> Add to Wishlist
            </Button>
          }
          prefill={{
            customer_name: deal.customer_name,
            customer_phone: deal.phone,
            customer_email: deal.email,
            deal_id: deal.id,
          }}
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <StageBadge stage={deal.stage} />
        <PriorityBadge priority={deal.priority} />
        <StatusDot status={deal.status} />
        {(() => {
          const m = computeMomentum(deal, dealActivities);
          const Icon = m.trend === 'up' ? TrendingUp : m.trend === 'down' ? TrendingDown : Minus;
          return (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${m.className}`}>
              <Icon className="h-3 w-3" /> {m.label}
            </span>
          );
        })()}
      </div>

      {/* Hero NEXT ACTION card */}
      {(() => {
        const isSold = deal.stage === 'sold';
        const lastMs = new Date(deal.last_activity_at).getTime();
        const daysSince = Number.isFinite(lastMs) ? Math.floor((Date.now() - lastMs) / 86400000) : 0;
        const isOverdue = !isSold && daysSince >= 1;
        const noNext = !deal.next_step && !isSold;

        const tone = isSold
          ? 'border-emerald-500/40 bg-emerald-500/5'
          : noNext
            ? 'border-primary/50 bg-primary/5 shadow-[0_0_40px_-14px_hsl(var(--primary)/0.6)]'
            : isOverdue
              ? 'border-primary/50 bg-primary/5 shadow-[0_0_40px_-14px_hsl(var(--primary)/0.55)]'
              : 'border-border bg-card';

        const headline = isSold
          ? 'Sold — log delivery'
          : noNext
            ? 'Set the next action'
            : NEXT_STEP_VERBS[deal.next_step as NextStep] ?? NEXT_STEP_LABELS[deal.next_step as NextStep];

        const Icon = isSold ? CheckCircle2 : noNext ? AlertOctagon : Target;

        return (
          <Card className={`border-2 ${tone} transition-shadow`}>
            <CardContent className="p-4 md:p-5">
              <div className="flex items-start gap-4">
                <div className={`shrink-0 h-10 w-10 rounded-lg flex items-center justify-center ${
                  isSold ? 'bg-emerald-500/15 text-emerald-400'
                  : 'bg-primary/15 text-primary'
                }`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
                      Next Action
                    </span>
                    {isOverdue && !noNext && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-primary/20 text-primary uppercase tracking-wider">
                        <AlertOctagon className="h-3 w-3" /> Overdue · {daysSince}d
                      </span>
                    )}
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold mt-0.5 truncate">{headline}</h2>
                  <p className="text-xs text-muted-foreground mt-1">
                    {isSold ? 'Confirm delivery date and capture CSI feedback.'
                      : noNext ? 'Every active deal needs a defined next step.'
                      : isOverdue ? `Last activity ${daysSince} day${daysSince === 1 ? '' : 's'} ago — momentum is slipping.`
                      : 'Stay on it — log the activity when you complete it.'}
                  </p>
                </div>
                <div className="shrink-0">
                  {noNext ? (
                    <Button size="sm" variant="default" className="gap-1.5"
                      onClick={() => document.getElementById('pipeline-next-step')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}>
                      Set next step <ArrowRight className="h-4 w-4" />
                    </Button>
                  ) : !isSold ? (
                    <Button size="sm" variant="default" className="gap-1.5" onClick={scrollToLog}>
                      Log it <ArrowRight className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" className="gap-1.5" onClick={scrollToLog}>
                      Log delivery <ArrowRight className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Stage chain — wraps on desktop, compact on mobile */}
      <Card>
        <CardContent className="p-3">
          {/* Mobile: previous → current → next */}
          <div className="sm:hidden flex items-center gap-2">
            {(() => {
              const idx = STAGE_ORDER.indexOf(deal.stage);
              const prev = idx > 0 ? STAGE_ORDER[idx - 1] : null;
              const next = idx < STAGE_ORDER.length - 1 ? STAGE_ORDER[idx + 1] : null;
              const Pill = ({ s, big = false, dim = false }: { s: DealStage; big?: boolean; dim?: boolean }) => (
                <span className={`px-2.5 py-1 rounded-md text-xs font-medium ${
                  big ? 'bg-primary text-primary-foreground'
                  : dim ? 'bg-muted/40 text-muted-foreground/70'
                  : 'bg-muted text-foreground'
                }`}>
                  {STAGE_LABELS[s]}
                </span>
              );
              return (
                <>
                  {prev && <><Pill s={prev} dim /><ChevronRight className="h-3 w-3 text-muted-foreground/50" /></>}
                  <Pill s={deal.stage} big />
                  {next && <><ChevronRight className="h-3 w-3 text-muted-foreground/50" /><Pill s={next} dim /></>}
                </>
              );
            })()}
          </div>
          {/* Desktop: full wrapped chain */}
          <ol className="hidden sm:flex flex-wrap items-center gap-1.5">
            {STAGE_ORDER.map((s, i) => {
              const currentIdx = STAGE_ORDER.indexOf(deal.stage);
              const isCurrent = s === deal.stage;
              const isComplete = i < currentIdx;
              return (
                <li key={s} className="flex items-center gap-1.5">
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium ${
                    isCurrent ? 'bg-primary text-primary-foreground'
                    : isComplete ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30'
                    : 'bg-muted/40 text-muted-foreground/70'
                  }`}>
                    {isComplete && <Check className="h-3 w-3" />}
                    {STAGE_LABELS[s]}
                  </span>
                  {i < STAGE_ORDER.length - 1 && (
                    <ChevronRight className={`h-3 w-3 ${i < currentIdx ? 'text-emerald-400/60' : 'text-muted-foreground/40'}`} />
                  )}
                </li>
              );
            })}
          </ol>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Customer Info</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3"><Phone className="h-4 w-4 text-muted-foreground" /><span className="text-sm">{deal.phone || '—'}</span></div>
            <div className="flex items-center gap-3"><Mail className="h-4 w-4 text-muted-foreground" /><span className="text-sm">{deal.email || '—'}</span></div>
            <div className="flex items-center gap-3"><Car className="h-4 w-4 text-muted-foreground" /><span className="text-sm">{deal.vehicle}</span></div>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setComposeChannel('email')}
                disabled={!deal.email}
                title={!deal.email ? 'No email on file' : 'Email customer from your account'}
              >
                <Mail className="h-3.5 w-3.5 mr-1.5" /> Email
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setComposeChannel('sms')}
                disabled={!deal.phone}
                title={!deal.phone ? 'No phone on file' : 'Text customer from your PullOver number'}
              >
                <MessageCircle className="h-3.5 w-3.5 mr-1.5" /> Text
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Deal Info</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Estimated Value</span>
              <span className="font-bold text-lg">${deal.estimated_value.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Rep</span>
              <span>{deal.rep_name}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Last Activity</span>
              <span>{timeAgo(deal.last_activity_at)}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      <LicenseScanCard imagePath={deal.license_image_path} data={deal.license_data} />

      <LinkedProgramsCard dealId={deal.id} dealershipId={deal.dealership_id} />

      <ConversationThreadCard
        dealId={deal.id}
        dealershipId={deal.dealership_id}
        customerName={deal.customer_name}
        customerEmail={deal.email ?? null}
        customerPhone={deal.phone ?? null}
      />

      {/* Editable fields */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Pipeline</CardTitle>
        </CardHeader>
        <CardContent className="grid md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Stage</label>
            <Select
              value={deal.stage}
              onValueChange={v => updateField('stage', v as DealStage)}
              disabled={savingField === 'stage'}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(STAGE_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</label>
            <Select
              value={deal.status}
              onValueChange={v => updateField('status', v as DealStatus)}
              disabled={savingField === 'status'}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2" id="pipeline-next-step">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Next Step</label>
            <Select
              value={deal.next_step ?? ''}
              onValueChange={v => updateField('next_step', v as NextStep)}
              disabled={savingField === 'next_step'}
            >
              <SelectTrigger>
                <SelectValue placeholder="Set next step" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(NEXT_STEP_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Notes</CardTitle>
          <div className="flex items-center gap-2">
            {previousSaved && notesDraft === lastSavedNotes.current && (
              <HoverCard openDelay={150}>
                <HoverCardTrigger asChild>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs gap-1 text-muted-foreground hover:text-foreground disabled:opacity-40"
                    onClick={() => requestUndo(previousSaved.value)}
                    disabled={undoSecondsLeft === 0 || notesStatus === 'saving'}
                  >
                    <Undo2 className="h-3 w-3" /> Undo last save
                    <span className="ml-1 tabular-nums text-muted-foreground/70">({undoSecondsLeft}s)</span>
                  </Button>
                </HoverCardTrigger>
                <HoverCardContent side="bottom" align="end" className="w-96">
                  <DiffPreview before={lastSavedNotes.current} after={previousSaved.value} />
                </HoverCardContent>
              </HoverCard>
            )}
            {isReal && notesDraft !== lastSavedNotes.current && (
              <HoverCard openDelay={150}>
                <HoverCardTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-xs gap-1"
                    onClick={() => saveNotes(notesDraft)}
                    disabled={notesStatus === 'saving'}
                  >
                    {notesStatus === 'saving'
                      ? <Loader2 className="h-3 w-3 animate-spin" />
                      : <Save className="h-3 w-3" />}
                    Save now
                    <kbd className="hidden sm:inline-block ml-1 px-1 py-0.5 rounded border border-border bg-muted font-mono text-[10px] text-muted-foreground/70">{modKey}+S</kbd>
                  </Button>
                </HoverCardTrigger>
                <HoverCardContent side="bottom" align="end" className="w-96">
                  <div className="space-y-2">
                    <DiffPreview before={lastSavedNotes.current} after={notesDraft} />
                    <p className="text-[11px] text-muted-foreground">Save now: <kbd className="font-mono">{modKey}+S</kbd></p>
                  </div>
                </HoverCardContent>
              </HoverCard>
            )}
            {(() => {
              const isDirty = notesDraft !== lastSavedNotes.current;
              let cls = 'border-border bg-muted text-muted-foreground';
              let icon = <Save className="h-3 w-3" />;
              let label: React.ReactNode = 'Autosave on';
              if (!isReal) {
                label = 'Read-only (Demo)';
              } else if (isUndoing) {
                cls = 'border-amber-500/30 bg-amber-500/10 text-amber-300';
                icon = <Loader2 className="h-3 w-3 animate-spin" />;
                label = 'Reverting…';
              } else if (notesStatus === 'saving') {
                cls = 'border-blue-500/30 bg-blue-500/10 text-blue-300';
                icon = <Loader2 className="h-3 w-3 animate-spin" />;
                label = 'Saving…';
              } else if (notesStatus === 'error') {
                cls = 'border-destructive/40 bg-destructive/10 text-destructive';
                icon = <AlertTriangle className="h-3 w-3" />;
                label = 'Save failed';
              } else if (isDirty) {
                cls = 'border-amber-500/30 bg-amber-500/10 text-amber-300';
                icon = <CircleDot className="h-3 w-3" />;
                label = 'Unsaved changes';
              } else if (notesStatus === 'saved' || lastSavedAt) {
                cls = 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300';
                icon = <Check className="h-3 w-3" />;
                label = (
                  <>Saved {lastSavedAt
                    ? (Date.now() - lastSavedAt.getTime() < 5_000
                        ? 'just now'
                        : timeAgo(lastSavedAt.toISOString()))
                    : ''}</>
                );
              }
              return (
                <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium ${cls}`}>
                  {icon}
                  {label}
                </span>
              );
            })()}
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          <Textarea
            value={notesDraft}
            onChange={e => {
              setNotesDraft(e.target.value);
              setNotesStatus('idle');
            }}
            placeholder="Add notes about this deal — autosaves as you type."
            rows={4}
            disabled={!isReal}
          />
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {lastSavedAt
                ? <>Last saved {timeAgo(lastSavedAt.toISOString())} · {lastSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</>
                : 'Not saved yet'}
            </span>
            {notesDraft !== lastSavedNotes.current && (
              <div className="flex items-center gap-3">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => setShowDiff(s => !s)}
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      {showDiff ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                      {showDiff ? 'Hide changes' : 'Show changes'}
                      <span className="hidden sm:inline-flex items-center gap-1 ml-1 text-[10px] text-muted-foreground/70">
                        <kbd className="px-1 py-0.5 rounded border border-border bg-muted font-mono">{modKey}+D</kbd>
                      </span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs">
                    Toggle diff: <kbd className="font-mono">{modKey}+D</kbd>
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => setConfirmUndo(true)}
                      className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
                    >
                      <Undo2 className="h-3 w-3" /> Undo to last saved
                      <span className="hidden sm:inline-flex items-center gap-1 ml-1 text-[10px] text-muted-foreground/70">
                        <kbd className="px-1 py-0.5 rounded border border-border bg-muted font-mono">{modKey}+⇧+Z</kbd>
                      </span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs">
                    <div className="space-y-1">
                      <div><kbd className="font-mono">{modKey}+Z</kbd> outside notes</div>
                      <div><kbd className="font-mono">{modKey}+Shift+Z</kbd> anywhere</div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </div>
            )}
          </div>
          {showDiff && notesDraft !== lastSavedNotes.current && (
            <div className="rounded-md border border-border bg-muted/30 p-3 text-sm font-mono whitespace-pre-wrap leading-relaxed">
              {diffWords(lastSavedNotes.current || '', notesDraft || '').map((part, i) => {
                if (part.added) {
                  return (
                    <span key={i} className="bg-emerald-500/20 text-emerald-300 rounded px-0.5">
                      {part.value}
                    </span>
                  );
                }
                if (part.removed) {
                  return (
                    <span key={i} className="bg-red-500/20 text-red-300 line-through rounded px-0.5">
                      {part.value}
                    </span>
                  );
                }
                return <span key={i}>{part.value}</span>;
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Fast Activity Log */}
      <Card ref={logCardRef}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Log Activity</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <TemplateChips
            dealId={deal.id}
            customerName={deal.customer_name}
            onLogged={refresh}
          />
          <Select value={activityType} onValueChange={v => setActivityType(v as ActivityType)}>
            <SelectTrigger><SelectValue placeholder="Activity type" /></SelectTrigger>
            <SelectContent>
              {Object.entries(DEMO_TYPE_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
              <SelectItem value="text_sent">Text Sent</SelectItem>
              <SelectItem value="email_sent">Email Sent</SelectItem>
              <SelectItem value="note_added">Note Added</SelectItem>
            </SelectContent>
          </Select>
          <Textarea
            placeholder="Quick notes (optional)"
            rows={2}
            value={activityNotes}
            onChange={e => setActivityNotes(e.target.value)}
          />
          <Button onClick={logActivity} disabled={logging || !activityType} className="w-full gap-2">
            {logging ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {(activityType && SAVE_LABELS[activityType as ActivityType]) || 'Save Activity'}
          </Button>
        </CardContent>
      </Card>

      {/* Activity Timeline */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Activity Timeline</CardTitle></CardHeader>
        <CardContent>
          {dealActivities.length === 0 ? (
            <p className="text-sm text-muted-foreground">No activity logged yet.</p>
          ) : (
            (() => {
              // Newest first
              const sorted = [...dealActivities].sort(
                (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
              );
              const isActiveDeal = deal.stage !== 'sold';
              return (
                <div className="relative pl-5">
                  <div className="absolute left-[7px] top-1 bottom-1 w-px bg-border/70" />
                  <ol className="space-y-3">
                    {sorted.map((act, i) => {
                      const isNewest = i === 0;
                      const ageMs = Date.now() - new Date(act.created_at).getTime();
                      const newestLabel = ageMs < 60_000 ? 'Just now' : timeAgo(act.created_at);
                      const dotColor = ACTIVITY_DOT_COLORS[act.activity_type] ?? 'bg-primary';
                      // Gap to the older neighbor
                      let gap: { hours: number; tone: 'amber' | 'red' } | null = null;
                      if (isActiveDeal && i < sorted.length - 1) {
                        const diffH = (new Date(act.created_at).getTime() - new Date(sorted[i + 1].created_at).getTime()) / 3_600_000;
                        if (diffH >= 72) gap = { hours: diffH, tone: 'red' };
                        else if (diffH >= 24) gap = { hours: diffH, tone: 'amber' };
                      }
                      const gapDays = gap ? Math.round(gap.hours / 24) : 0;
                      return (
                        <li key={act.id}>
                          <div className={`relative -ml-5 pl-5 rounded-md py-1.5 pr-2 ${isNewest ? 'bg-muted/30' : ''}`}>
                            <span className={`absolute left-[3px] top-2.5 h-2.5 w-2.5 rounded-full ring-2 ring-background ${dotColor}`} />
                            <div className="flex items-baseline justify-between gap-3">
                              <p className="text-sm font-medium">{actLabel(act.activity_type)}</p>
                              <p className={`text-[11px] tabular-nums shrink-0 ${isNewest ? 'text-foreground font-semibold' : 'text-muted-foreground/60'}`}>
                                {isNewest ? newestLabel : timeAgo(act.created_at)}
                              </p>
                            </div>
                            {act.details && <p className="text-sm text-muted-foreground mt-0.5">{act.details}</p>}
                            <p className="text-[10px] text-muted-foreground/50 mt-0.5">{act.rep_name}</p>
                          </div>
                          {gap && (
                            <div className={`-ml-5 pl-5 my-1.5 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider ${
                              gap.tone === 'red' ? 'text-primary' : 'text-yellow-400'
                            }`}>
                              <span className={`h-px flex-1 ${gap.tone === 'red' ? 'bg-primary/40' : 'bg-yellow-400/40'} border-dashed`}
                                style={{ borderTop: `1px dashed ${gap.tone === 'red' ? 'hsl(var(--primary)/0.5)' : 'hsl(48 96% 53% / 0.5)'}`, background: 'transparent', height: 0 }} />
                              <span>↑ {gapDays} day{gapDays === 1 ? '' : 's'} quiet</span>
                              <span className={`h-px flex-1`} style={{ borderTop: `1px dashed ${gap.tone === 'red' ? 'hsl(var(--primary)/0.5)' : 'hsl(48 96% 53% / 0.5)'}`, background: 'transparent', height: 0 }} />
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                </div>
              );
            })()
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirmUndo} onOpenChange={setConfirmUndo}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              Your current edits will be replaced with the last saved version of these notes. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setNotesDraft(lastSavedNotes.current);
                setShowDiff(false);
                setNotesStatus('idle');
                toast.success('Reverted to last saved version');
              }}
            >
              Revert notes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mobile sticky +Log — keeps the most-used action one tap away. */}
      <button
        type="button"
        onClick={scrollToLog}
        aria-label="Log activity"
        className="lg:hidden fixed bottom-20 right-4 z-40 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center active:scale-95 transition-transform"
      >
        <Plus className="h-6 w-6" />
      </button>

      {composeChannel && (
        <ComposeMessageDialog
          open={!!composeChannel}
          onOpenChange={(o) => { if (!o) setComposeChannel(null); }}
          channel={composeChannel}
          dealId={deal.id}
          dealershipId={deal.dealership_id}
          customerName={deal.customer_name}
          to={composeChannel === 'email' ? (deal.email ?? '') : (deal.phone ?? '')}
          defaultSubject={`Following up — ${deal.vehicle}`}
          onSent={() => { /* trigger refresh via realtime / nothing; activity auto-logged via DB trigger */ }}
        />
      )}
    </div>
  );
}